// client/src/coms/views/watch_together_parts/remoteSpotifyPlaybackRouter.js

const REMOTE_SPOTIFY_PACKET_TOPIC = 'pa-watch-spotify-remote';
const REMOTE_SPOTIFY_PACKET_TYPE = 'pa_watch_spotify_remote_v1';

const listeners = new Set();

function safeId(value) {
  return String(value || '').trim();
}

function safeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function safeProvider(value) {
  const provider = safeId(value).toLowerCase();
  if (provider === 'spotify') return 'spotify';
  return '';
}

function safeSpotifyType(value) {
  const type = safeId(value).toLowerCase();
  if (type === 'track' || type === 'album' || type === 'playlist' || type === 'episode' || type === 'show' || type === 'artist') return type;
  return '';
}

function textEncode(value) {
  return new TextEncoder().encode(JSON.stringify(value));
}

function textDecode(payload) {
  return JSON.parse(new TextDecoder().decode(payload));
}

function normalizeRemoteSpotifyEvent(raw, participant = null) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.__pa !== REMOTE_SPOTIFY_PACKET_TYPE) return null;

  const provider = safeProvider(raw.pr || raw.provider);
  if (provider !== 'spotify') return null;

  const mediaId = safeId(raw.mid || raw.mediaId);
  const mediaType = safeSpotifyType(raw.mt || raw.mediaType);
  const mediaUri = safeId(raw.mu || raw.mediaUri || (mediaType && mediaId ? `spotify:${mediaType}:${mediaId}` : ''));
  const action = safeId(raw.ac || raw.action).toLowerCase();
  const serverId = safeId(raw.sid || raw.serverId);
  const fromIdentity = safeId(raw.fi || raw.fromIdentity || (participant && participant.identity));

  if (!mediaId || !mediaType || !mediaUri) return null;
  if (action !== 'play' && action !== 'pause' && action !== 'progress') return null;

  return {
    provider: 'spotify',
    serverId,
    mediaId,
    mediaType,
    mediaUri,
    currentTime: Math.max(0, safeNumber(raw.ct != null ? raw.ct : raw.currentTime, 0)),
    action,
    updatedAt: safeNumber(raw.ts != null ? raw.ts : raw.updatedAt, Date.now()),
    fromIdentity,
  };
}

function emit(event) {
  listeners.forEach((listener) => {
    try {
      listener(event);
    } catch {}
  });

  try {
    window.__VOX_REMOTE_SPOTIFY_PLAYBACK = event;
  } catch {}
}

export function subscribeRemoteSpotifyPlayback(listener) {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export async function publishRemoteSpotifyPlayback({ serverId, mediaId, mediaType, mediaUri, currentTime, action }, preferredRoom = null) {
  const room = preferredRoom && typeof preferredRoom === 'object' ? preferredRoom : null;
  const localParticipant = room && room.localParticipant ? room.localParticipant : null;
  if (!localParticipant || typeof localParticipant.publishData !== 'function') return false;

  const destinationIdentities = [];
  try {
    room.remoteParticipants.forEach((participant) => {
      const identity = safeId(participant && participant.identity);
      if (identity) destinationIdentities.push(identity);
    });
  } catch {}

  if (!destinationIdentities.length) return true;

  const packet = {
    __pa: REMOTE_SPOTIFY_PACKET_TYPE,
    sid: safeId(serverId),
    pr: 'spotify',
    mid: safeId(mediaId),
    mt: safeSpotifyType(mediaType),
    mu: safeId(mediaUri),
    ct: Math.max(0, safeNumber(currentTime, 0)),
    ac: safeId(action).toLowerCase(),
    ts: Date.now(),
    fi: safeId(localParticipant.identity),
  };

  if (!packet.sid || !packet.mid || !packet.mt || !packet.mu || (packet.ac !== 'play' && packet.ac !== 'pause' && packet.ac !== 'progress')) {
    return false;
  }

  const bytes = textEncode(packet);

  try {
    await localParticipant.publishData(bytes, {
      reliable: true,
      topic: REMOTE_SPOTIFY_PACKET_TOPIC,
      destinationIdentities,
    });
    return true;
  } catch {}

  try {
    await localParticipant.publishData(bytes, {
      reliable: true,
      destinationIdentities,
    });
    return true;
  } catch {}

  return false;
}

export function applyIncomingRemoteSpotifyPlaybackPacket(payload, participant, topic) {
  const maybeTopic = safeId(topic);
  if (maybeTopic && maybeTopic !== REMOTE_SPOTIFY_PACKET_TOPIC) return false;

  let raw = null;
  try {
    raw = textDecode(payload);
  } catch {
    return false;
  }

  const normalized = normalizeRemoteSpotifyEvent(raw, participant);
  if (!normalized) return false;

  emit(normalized);
  return true;
}
